import React from 'react'
import { GlobalDataContext } from './GlobalDataProvider'
import { WidgetContainerContext } from './WidgetContainerProvider'

export const useGlobalDataContext = (providerName = 'useGlobalStore') => {
  const context = React.useContext(GlobalDataContext)

  if (!context) {
    throw new Error(`${providerName}() must be used within the GlobalDataContext`)
  }
  return context
}

export const useAccountStore = () =>
  useGlobalDataContext('useAccountStore').globalStore.accountStore
export const useAppConfig = () =>
  useGlobalDataContext('useAppConfig').globalStore.appDataStore.appConfig
export const useAnalyticsStore = () =>
  useGlobalDataContext('useAnalyticsStore').globalStore.analyticsStore
export const useBudgetsStore = () =>
  useGlobalDataContext('useBudgetsStore').globalStore.budgetsStore
export const useCashflowStore = () =>
  useGlobalDataContext('useCashflowStore').globalStore.cashflowStore
export const useCategoryStore = () =>
  useGlobalDataContext('useCategoryStore').globalStore.categoryStore
export const useConnectStore = () =>
  useGlobalDataContext('useConnectStore').globalStore.connectStore
export const useDebtStore = () => useGlobalDataContext('useDebtsStore').globalStore.debtsStore
export const useFinstrongStore = () =>
  useGlobalDataContext('useFinstrongStore').globalStore.finstrongStore
export const useGlobalStore = () => useGlobalDataContext('useGlobalStore').globalStore
export const useGlobalUiStore = () =>
  useGlobalDataContext('useGlobalUiStore').globalStore.globalUiStore
export const useGlobalCopyStore = () =>
  useGlobalDataContext('globalCopyStore').globalStore.copyStore.copy
export const useGoalStore = () => useGlobalDataContext('useGoalStore').globalStore.goalStore
export const useHelpStore = () => useGlobalDataContext('useHelpStore').globalStore.helpStore
export const useHoldingStore = () =>
  useGlobalDataContext('useHoldingStore').globalStore.holdingStore
export const useInsightsV2Store = () =>
  useGlobalDataContext('useInsightsV2Store').globalStore.insightsV2Store
// Typed accessor for the action drawer's opaque, block-specific context. The
// reading component must be an `observer` (the store read is what tracks it).
export const useActionContext = <T,>(): T | undefined =>
  useGlobalDataContext('useActionContext').globalStore.insightsV2Store.actionDrawer.context as
    | T
    | undefined
export const useMerchantStore = () =>
  useGlobalDataContext('useMerchantStore').globalStore.merchantStore
export const useNetWorthStore = () =>
  useGlobalDataContext('useNetWorthStore').globalStore.netWorthStore
export const useNotificationStore = () =>
  useGlobalDataContext('useNotificationStore').globalStore.notificationStore
export const useRecurringTransactionsStore = () =>
  useGlobalDataContext('useRecurringTransactionsStore').globalStore.recurringTransactionsStore
export const useSettingsStore = () =>
  useGlobalDataContext('useSettingsStore').globalStore.settingsStore
export const useTransactionStore = () =>
  useGlobalDataContext('useTransactionStore').globalStore.transactionStore
export const useTrendsStore = () => useGlobalDataContext('useTrendsStore').globalStore.trendsStore
export const useUserStore = () => useGlobalDataContext('useUserStore').globalStore.userStore

export const useError = () => ({ onError: useGlobalDataContext().onError })
export const useEvent = () => ({ onEvent: useGlobalDataContext().onEvent })
export const useLoad = () => ({ onLoad: useGlobalDataContext().onLoad })
export const usePageView = () => ({ onPageView: useGlobalDataContext().onPageView })

export const useWidgetContainerContext = (providerName = 'useWidgetContainerContext') => {
  const context = React.useContext(WidgetContainerContext)

  if (!context) {
    throw new Error(`${providerName}() must be used within the WidgetContainerContext`)
  }

  return context
}

export const useWidgetContainerProvider = () => useWidgetContainerContext()
